/**
 * Schedule Signing Modal
 * Modal for scheduling contract signing appointments with clients
 */

import React, { useState } from 'react';
import { signingAPI } from '../../services/api';
import DateTimePickerDropdown from '../common/DateTimePickerDropdown';

const ScheduleSigningModal = ({ isOpen, onClose, projectId, onSuccess }) => {
  const [appointmentData, setAppointmentData] = useState({
    scheduledDateTime: null,
    location: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  // Validate inputs
  const validateInputs = () => {
    if (!appointmentData.scheduledDateTime) {
      setError('Please select an appointment date and time');
      return false;
    }
    if (!appointmentData.location.trim()) {
      setError('Please specify the meeting location');
      return false;
    }
    return true;
  };

  // Handle scheduling
  const handleSchedule = async () => {
    if (!validateInputs()) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await signingAPI.schedule({
        designProjectId: projectId,
        scheduledDate: appointmentData.scheduledDateTime.toISOString(),
        location: appointmentData.location,
        signingType: 'in_person',
        notes: appointmentData.notes,
        reminderDays: 1
      });

      // Success callback
      if (onSuccess) {
        onSuccess({
          appointmentId: response.data.appointmentId,
          ...appointmentData,
          scheduledDateTime: appointmentData.scheduledDateTime.toISOString()
        });
      }

      handleClose();
    } catch (err) {
      console.error('Error scheduling appointment:', err);
      setError(err.message || 'Failed to schedule signing appointment');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setAppointmentData({
      scheduledDateTime: null,
      location: '',
      notes: ''
    });
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-teal-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="text-xl font-semibold">Schedule Signing Appointment</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Info box */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <p className="text-teal-800">
              Schedule a contract signing appointment with the client.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Date and Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date & Time *
            </label>
            <DateTimePickerDropdown
              value={appointmentData.scheduledDateTime}
              onChange={(dateTime) => setAppointmentData(prev => ({ ...prev, scheduledDateTime: dateTime }))}
              minDate={new Date()}
              disabled={submitting}
              placeholder="Select appointment date & time"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Location *
            </label>
            <input
              type="text"
              value={appointmentData.location}
              onChange={(e) => setAppointmentData(prev => ({ ...prev, location: e.target.value }))}
              disabled={submitting}
              placeholder="e.g., TENQ Office, Client's Home, Project Site"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={appointmentData.notes}
              onChange={(e) => setAppointmentData(prev => ({ ...prev, notes: e.target.value }))}
              disabled={submitting}
              rows={3}
              placeholder="Add any special instructions or notes for the appointment..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={submitting}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Scheduling...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Schedule Appointment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSigningModal;
